/**
 * The rival's mind (docs/research/C0_PVP.md §3). A pure function: it sees what a person at the screen would see (the
 * other fighter's position, guard, wind-up and stagger), but a short time late (the reaction time of its level), and
 * answers with the same things a person can do: walk, guard, use a move, step aside (dash), keep hitting (engage).
 * `systems/duelMatch.ts` turns the answer into `PlayerCommand`s sent as the rival's own, so a real player can take this
 * seat and nothing else changes.
 *
 * What makes it read as a person and not a script:
 * - it reads wind-ups late (`DUEL_REACT`) and gets a read wrong now and then (`DUEL_SKILL`), more at low levels;
 * - it guards on a guess (the foe is close and has been swinging), in bursts, and lets the guard go to bait;
 * - against a raised guard it reaches for the guard-breaking move, but only when it dares (the break has a wind-up);
 * - against a break wind-up it cuts in with a quick strike (the interrupt) or steps aside;
 * - it feints: steps in and out of range, drops and raises the guard, to draw a move out;
 * - it punishes a stagger, and it will not stand still: it strafes, and it steps off after taking a hit.
 * All draws come from the injected `rnd`; time is only the `t` it is handed.
 */
import type { SkillKind } from '../data/skills';
import { DUEL_REACT, DUEL_SKILL, type DuelStyle } from '../data/duelMatch';

export interface FighterView {
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  stamina: number;
  maxStamina: number;
  /** แต้มการ์ด, its most, and the seconds left of a broken guard (no guard at all). */
  gp: number;
  gpMax: number;
  brokenT: number;
  /** Seconds left of a counter stance. */
  counterT: number;
  blocking: boolean;
  stunT: number;
  swingT: number;
  dashT: number;
  /** Seconds of recovery left after a move (no guard, no dodge, no move): the opening to punish. */
  recovery?: number;
  /** Height above the ground (px; 0 on it), and whether a dive strike is under way (P1.5). */
  z?: number;
  diving?: boolean;
  airDashed?: boolean;
  /** The wind-up under way: its kind and the seconds still to run. */
  cast: { kind: SkillKind; left: number; big?: boolean; ult?: boolean } | null;
}

export interface BotSkill {
  readonly slot: number;
  readonly id: string;
  readonly kind: SkillKind;
  readonly range: number;
  readonly windup: number;
  readonly cooldown?: number;
  readonly power: number;
  /** A gap-closer: it carries the user to the foe. */
  readonly dash: boolean;
  /** A buff that opens a counter stance (data/duelMatch.ts `DUEL_COUNTERS`). */
  readonly counter?: boolean;
  /** A buff whose status is already on the caster (the bot does not recast it). */
  readonly active?: boolean;
  /** Off cooldown, enough qi and rage. */
  readonly ready: boolean;
  /** An ultimate (costs the full rage bar): the bot spends it by its style, never as an ordinary move (P2c). */
  readonly ultimate?: boolean;
}

export interface BotView {
  t: number;
  me: FighterView;
  foe: FighterView;
  skills: readonly BotSkill[];
  /** Enough stamina to step aside. */
  canDash: boolean;
  /** Enough พลังกาย for the combo escape and an air dash is known (P1.5). */
  canEscape?: boolean;
  canAirDash?: boolean;
  /** The family's bot numbers (data/weaponFamilies.ts): how far its basic blow counts as in reach, and the least spacing it keeps. Missing: 2 / 1.5. */
  reach?: number;
  floor?: number;
}

interface Snap {
  t: number;
  dist: number;
  castKind: SkillKind | null;
  castBig: boolean;
  castUlt: boolean;
  castLeft: number;
  blocking: boolean;
  stunned: boolean;
  broken: boolean;
  counter: boolean;
  gp: number;
  swinging: boolean;
  recovering: boolean;
}

export interface BotMem {
  nextT: number;
  hist: Snap[];
  /** Guard held until this time (a burst). */
  guardUntil: number;
  /** Do not guard again before this time (so the guard is not a wall). */
  guardRest: number;
  strafe: 1 | -1;
  strafeUntil: number;
  /** A feint under way: step 'in' or 'out' until this time. */
  feint: 'in' | 'out' | null;
  feintUntil: number;
  /** The last time the foe was seen swinging, and my health a moment ago (to notice a hit taken). */
  foeSwungAt: number;
  hpSeen: number;
  hpSeenAt: number;
  /** Do not step aside again before this time. */
  dashRest: number;
  /** A held decision (so it does not change its mind every tick): hold off attacking until. */
  waitUntil: number;
  /** The jump-in under way: 0 none, 1 jumped (dash in the air next), 2 dashed (dive next). */
  air: 0 | 1 | 2;
  /** The time of the first thought (the bout clock the bot reads for its impatience). */
  startT?: number;
}

export const newBotMem = (): BotMem => ({
  nextT: 0,
  hist: [],
  guardUntil: 0,
  guardRest: 0,
  strafe: 1,
  strafeUntil: 0,
  feint: null,
  feintUntil: 0,
  foeSwungAt: -9,
  hpSeen: 1,
  hpSeenAt: 0,
  dashRest: 0,
  waitUntil: 0,
  air: 0,
});

export interface BotAct {
  /** Map-space walk direction (unit, or 0,0 to stand). */
  move: readonly [number, number];
  block: boolean;
  /** Bar slot of a move to start, or null. */
  skill: number | null;
  /** Step aside along this direction, or null. */
  dash: readonly [number, number] | null;
  /** Keep cutting with the basic attack while in reach. */
  engage: boolean;
  /** Jump (the start of a jump-in). */
  jump?: boolean;
  /** Press the basic cut now (in the air after an air dash it is the dive strike). */
  attack?: boolean;
}

const IDLE: BotAct = { move: [0, 0], block: false, skill: null, dash: null, engage: false };

/** How often the rival thinks (s): a person does not decide sixty times a second. */
export const BOT_THINK = 0.1;
/** Extra say over which of the bot's moves count as ready (systems/duelBotLock.ts keeps lock moves for its punishes). */
export const BOT_SKILL_GATES: ((w: object, botId: number, skillId: string) => boolean)[] = [];
/** Last say over what the bot does this thought (systems/duelBotRing.ts: keep in the ring, hold the edge, back off to heal). */
export const BOT_ACT_FILTERS: ((w: object, botId: number, act: BotAct) => BotAct)[] = [];
const BASIC_REACH = 2;

const unit = (dx: number, dy: number): readonly [number, number] => {
  const l = Math.hypot(dx, dy) || 1;
  return [dx / l, dy / l];
};

/**
 * The power tell (violet ring): read at `read` × the reaction time, answered `dodgeBonus` more often than other tells
 * (negative: less often; the ring is read early and the dash rest is waived, so a full answer rate dodged ~67%).
 * Tuned by ns-balance (scripts/balance/powerTell.ts): a power cast's first outcome is a dodge ~42% (band 35-50%).
 * A plain object so the balance sims can sweep it in memory; game code only reads it.
 */
export const POWER_TELL = { read: 0.4, dodgeBonus: -0.375 };
/**
 * An ultimate's wind-up (the full rage bar) is read as a power tell, answered `base` + `evade` x the style's evade more
 * often (a power move cannot be guarded, so the answer is a step aside). Duel v3 P2c, aimed at 25-40% avoided.
 */
export const ULT_TELL = { base: 0.15, evade: 0.2 };

/** Offence pace in the fighting range (ns-balance R6: bouts timed out 21-25%; was base 0.12, aggr 0.3, pause 1, hold 0.1, no late push). */
export const STRIKE = { base: 0.3, aggr: 0.5, pause: 0.5, hold: 0.02, late: 25, push: 0.6 };

export function thinkBot(
  v: BotView,
  mem: BotMem,
  style: DuelStyle,
  level: number,
  rnd: () => number,
): BotAct | null {
  if (v.t < mem.nextT) return null;
  mem.nextT = v.t + BOT_THINK + rnd() * 0.04;

  const { me, foe } = v;
  const reachB = v.reach ?? BASIC_REACH;
  const floorD = v.floor ?? 1.5;
  const lv = Math.max(0, Math.min(DUEL_REACT.length - 1, level));
  const react = DUEL_REACT[lv]!;
  // A new fighter presses less often and chains less; a master is on you.
  mem.startT ??= v.t;
  // Behind or level on health once the clock is running down: press (a person who is not winning on the clock must attack).
  const press = v.t - mem.startT > STRIKE.late && me.hp / Math.max(1, me.maxHp) <= foe.hp / Math.max(1, foe.maxHp) + 0.02;
  const aggr = style.aggression * (0.45 + 0.12 * lv) + (press ? STRIKE.push : 0);
  const sharp = DUEL_SKILL[lv]!;
  const dx = foe.x - me.x;
  const dy = foe.y - me.y;
  const dist = Math.hypot(dx, dy);
  const [ux, uy] = unit(dx, dy);

  // What I can see now goes into the history; what I act on is what I saw `react` seconds ago.
  const swinging = foe.swingT > 0 || (foe.cast !== null && foe.cast.kind !== 'break');
  if (swinging) mem.foeSwungAt = v.t;
  mem.hist.push({
    t: v.t,
    dist,
    castKind: foe.cast?.kind ?? null,
    castBig: !!foe.cast?.big || foe.cast?.kind === 'power',
    castUlt: !!foe.cast?.ult,
    castLeft: foe.cast?.left ?? 0,
    blocking: foe.blocking,
    stunned: foe.stunT > 0,
    broken: foe.brokenT > 0,
    counter: foe.counterT > 0,
    gp: foe.gp / Math.max(1, foe.gpMax),
    swinging,
    recovering: (foe.recovery ?? 0) > 0.08,
  });
  while (mem.hist.length > 2 && mem.hist[1]!.t <= v.t - react) mem.hist.shift();
  const seen = mem.hist[0]!;
  const seenAge = v.t - seen.t;

  // Hit taken since a moment ago?
  const hpNow = me.hp / me.maxHp;
  const justHit = mem.hpSeen - hpNow > 0.04;
  if (v.t - mem.hpSeenAt > 0.45 || justHit) {
    mem.hpSeen = hpNow;
    mem.hpSeenAt = v.t;
  }

  // The combo escape (สลัดหลุด): a hit-stunned fighter breaks out with a back-step, more often at higher levels.
  if (me.stunT > 0 && me.brokenT <= 0 && v.canEscape && rnd() < 0.035 * lv + 0.01) return { ...IDLE, dash: [-ux, -uy] };

  // Nothing to decide while staggered, winding up or mid-step.
  if (me.stunT > 0 || me.cast || me.dashT > 0 || (me.recovery ?? 0) > 0) return { ...IDLE };

  // In the air after my jump: dash at the foe, then dive on it (the jump-in).
  if ((me.z ?? 0) > 0) {
    if (mem.air === 1 && v.canAirDash && dist > 2.5) {
      mem.air = 2;
      return { ...IDLE, dash: [ux, uy] };
    }
    if (mem.air >= 1) {
      mem.air = 0;
      return { ...IDLE, attack: true };
    }
    return { ...IDLE };
  }
  mem.air = 0;

  const ready = (k: SkillKind) => v.skills.filter((s) => s.kind === k && s.ready && !s.ultimate);
  const inReach = (s: BotSkill) => dist <= s.range + 0.2;
  const away = (sign = 1): readonly [number, number] => [-ux * sign, -uy * sign];
  const side = (): readonly [number, number] => {
    if (v.t >= mem.strafeUntil) {
      mem.strafe = rnd() < 0.5 ? 1 : -1;
      mem.strafeUntil = v.t + 0.7 + rnd() * 0.9;
    }
    return [-uy * mem.strafe, ux * mem.strafe];
  };
  const step = (dir: readonly [number, number]): BotAct | null => {
    if (!v.canDash || v.t < mem.dashRest) return null;
    mem.dashRest = v.t + 0.9 + rnd() * 0.8;
    mem.guardUntil = 0;
    return { ...IDLE, dash: dir };
  };

  // My guard is broken: no guard to stand behind. Keep away and wait it out.
  if (me.brokenT > 0 && dist < 4) {
    const d = rnd() < 0.6 ? step(away()) : null;
    return d ?? { ...IDLE, move: away() };
  }

  // A slip: now and then it simply does not see it (more at low levels).
  if (rnd() < (1 - sharp) * 0.08) return { ...IDLE, move: side() };

  const guardOk = me.brokenT <= 0 && me.gp > me.gpMax * 0.2;

  // ---- 1. A break wind-up in front of me (the tell). Answer it: cut in under it, or step out of it. ----
  if (seen.castKind === 'break' && seen.dist <= 3.6 && seenAge < 0.9) {
    if (rnd() < sharp) {
      const quick = ready('attack').filter((s) => inReach(s) && s.windup <= 0.1);
      // The cut must land before the wind-up ends: about a fifth of a second.
      const time = seen.castLeft - seenAge;
      if (quick.length && time > 0.2 && rnd() < 0.65) {
        mem.guardUntil = 0;
        return { ...IDLE, skill: quick[0]!.slot };
      }
      const d = step(rnd() < 0.5 ? side() : away());
      if (d) return d;
    }
  }

  // ---- 1b. A big move winding up (a flurry, a gap-closer, a heavy blow, a power move): a tell to answer. ----
  // The violet ring of a power move is the clearest tell in the bout, so it is read faster than the rest (a person
  // sees a ring appear quicker than a stance change) and stepped out of even when the dash is still resting.
  const fresh = mem.hist.find((h) => h.castKind === 'power' && h.t <= v.t - react * POWER_TELL.read);
  const bigSeen = fresh && seen.castKind !== 'break' ? fresh : seen;
  if (bigSeen.castBig && bigSeen.castKind !== 'break' && bigSeen.dist <= 6 && v.t - bigSeen.t < 1) {
    const tell =
      (bigSeen.castKind === 'power' ? POWER_TELL.dodgeBonus : 0) +
      (bigSeen.castUlt ? ULT_TELL.base + ULT_TELL.evade * style.evade : 0);
    if (rnd() < sharp + tell) {
      // A power move cannot be blocked: step aside. Anything else: guard it, or step aside.
      if (bigSeen.castKind !== 'power' && guardOk && rnd() < 0.55) {
        mem.guardUntil = v.t + Math.max(0.3, bigSeen.castLeft - (v.t - bigSeen.t)) + 0.45;
        mem.guardRest = mem.guardUntil + 0.4;
        return { ...IDLE, block: true };
      }
      if (bigSeen.castKind === 'power' && v.canDash) mem.dashRest = Math.min(mem.dashRest, v.t);
      const d = step(rnd() < 0.5 ? side() : away());
      if (d) return d;
    }
  }

  // ---- 1c. A foe in the air coming at me (a jump-in): guard it or step out from under it (anti-air). ----
  if ((foe.z ?? 0) > 0 && dist <= 5 && rnd() < sharp * 0.8) {
    if (foe.diving || foe.airDashed) {
      if (guardOk && rnd() < 0.4) {
        mem.guardUntil = v.t + 0.6;
        mem.guardRest = mem.guardUntil + 0.4;
        return { ...IDLE, block: true };
      }
      const d = step(side());
      if (d) return d;
    }
  }

  // ---- 2a. The ultimate (full rage bar), spent by style. Its wind-up and violet ring stay the foe's tell. ----
  const ult = v.skills.find((s) => s.ultimate && s.ready && dist <= s.range + 0.2);
  if (ult) {
    const open = seen.stunned || seen.broken || seen.recovering;
    const foeLow = foe.hp / Math.max(1, foe.maxHp) < 0.25;
    const use = style.rageUse ?? 'spend';
    let fire: boolean;
    if (use === 'spend') fire = rnd() < 0.6 + 0.3 * sharp;
    else if (use === 'hold') fire = (open && rnd() < 0.6 * sharp) || (foeLow && rnd() < 0.5);
    else fire = (open && rnd() < 0.6 * sharp) || (seen.blocking && rnd() < 0.2) || rnd() < 0.015 + 0.03 * style.feint;
    if (fire) {
      mem.guardUntil = 0;
      return { ...IDLE, skill: ult.slot };
    }
  }

  // ---- 2. Staggered foe: punish. ----
  if ((seen.stunned || seen.broken) && dist < 6) {
    const hits = [...ready('attack'), ...ready('power')]
      .filter(inReach)
      .sort((a, b) => b.power - a.power);
    if (hits.length) return { ...IDLE, skill: hits[0]!.slot };
    if (dist > reachB) {
      const rush = ready('attack').find((s) => s.dash && dist <= s.range);
      if (rush) return { ...IDLE, skill: rush.slot };
      return { ...IDLE, move: [ux, uy] };
    }
    return { ...IDLE, engage: true };
  }

  // ---- 2b. A foe in recovery (after a move, most of all a whiffed break) cannot guard or dodge: punish it, by skill. ----
  if (seen.recovering && !seen.stunned && dist < 5 && rnd() < sharp) {
    const hits = [...ready('attack'), ...ready('power')]
      .filter((x) => inReach(x) && (x.windup ?? 0) <= 0.5)
      .sort((a, b) => b.power - a.power);
    if (hits.length) {
      mem.guardUntil = 0;
      return { ...IDLE, skill: hits[0]!.slot };
    }
    if (dist <= reachB + 0.3) return { ...IDLE, engage: true };
    return { ...IDLE, move: [ux, uy] };
  }

  // ---- 3. Guard: a burst on a guess, never a wall. ----
  const foeNear = dist < 3.2;
  const recentlySwung = v.t - mem.foeSwungAt < 1.1;
  if (mem.guardUntil > v.t) {
    // Drop it: a break coming (it would shatter it), the foe backed off, or it wore thin.
    // A break seen is read by skill: a weaker read keeps the guard up and bites (the guard shatters).
    const breakRead = seen.castKind === 'break' && rnd() < sharp;
    if (breakRead || !foeNear || !guardOk) {
      mem.guardUntil = 0;
      mem.guardRest = v.t + 0.5;
    } else return { ...IDLE, block: true, move: [0, 0] };
  } else if (foeNear && recentlySwung && guardOk && v.t >= mem.guardRest && !foe.blocking) {
    if (rnd() < style.guard * (0.5 + 0.5 * sharp) * 0.55) {
      mem.guardUntil = v.t + 0.45 + rnd() * 0.5;
      mem.guardRest = mem.guardUntil + 0.45;
      return { ...IDLE, block: true };
    }
  }

  // ---- 4. Hit just now: sometimes step off (do not stand and trade). ----
  if (justHit && rnd() < style.evade * 0.6) {
    const d = step(rnd() < 0.6 ? away() : side());
    if (d) return d;
  }

  // ---- 5. Heal when hurt and left alone. ----
  const heal = ready('heal')[0];
  if (heal && hpNow < 0.45 && dist > 5) return { ...IDLE, skill: heal.slot };

  // ---- 6a. The foe has a counter stance open: do not hit into it. Wait it out, or cut through it with a break. ----
  if (seen.counter && dist < 4) {
    if (rnd() < sharp) {
      const brk = ready('break').filter(inReach)[0];
      if (brk && rnd() < 0.5) return { ...IDLE, skill: brk.slot };
      return { ...IDLE, move: dist < 2 ? away(0.7) : side() };
    }
  }

  // ---- 6b. A raised guard in front of me: it only cuts the damage, so keep the pressure on its guard points. ----
  if (seen.blocking && dist < 3.4) {
    const brk = ready('break').filter(inReach)[0];
    // The break shatters the guard at once.
    if (brk && rnd() < style.breaker * 0.6) return { ...IDLE, skill: brk.slot };
    const power = ready('power').filter(inReach)[0];
    if (power && rnd() < 0.5) return { ...IDLE, skill: power.slot };
    // A guard running low: the heaviest blows in reach finish it.
    const heavy = [...ready('attack')].filter((x) => inReach(x) && !x.dash).sort((a, b) => b.power - a.power)[0];
    if (heavy && (seen.gp < 0.5 || rnd() < aggr * 0.45)) return { ...IDLE, skill: heavy.slot };
    if (dist <= reachB + 0.3 && rnd() < 0.2 + aggr * 0.3) return { ...IDLE, engage: true };
    // Otherwise circle: the guard points come back only while the guard is down.
    if (dist < 1.6) return { ...IDLE, move: away(0.5) };
    return { ...IDLE, move: side() };
  }

  // ---- 6c. A counter stance of my own, on a hunch that the foe is about to swing (a bait). ----
  if (me.counterT > 0) return { ...IDLE };
  const stance = ready('buff').find((x) => x.counter);
  if (stance && dist < 3.4 && recentlySwung && !seen.stunned && !seen.broken && !seen.counter) {
    if (rnd() < style.counter * (0.35 + 0.65 * sharp) * 0.5) return { ...IDLE, skill: stance.slot };
  }

  // ---- 6d. A buff move (not a counter stance): cast it before the exchange, never while the foe is on me or the buff still runs. ----
  const buff = ready('buff').find((x) => !x.counter && !x.active);
  if (buff && dist > 2.4 && !seen.stunned && !seen.broken && !seen.swinging && rnd() < 0.2 + 0.4 * sharp) return { ...IDLE, skill: buff.slot };

  // ---- 7. A feint under way: in or out of range without striking. ----
  if (mem.feint && v.t < mem.feintUntil) {
    return { ...IDLE, move: mem.feint === 'in' ? [ux, uy] : away(), block: mem.feint === 'out' && rnd() < 0.3 };
  }
  mem.feint = null;

  // ---- 8. Spacing and offence. ----
  // The wanted distance is the style's, but never farther than the build's own blows reach.
  // (the blows it can throw often: a move on a long cooldown does not set the distance)
  const reachMax = Math.max(
    reachB,
    ...v.skills.filter((x) => x.kind === 'attack' && !x.dash && (x.cooldown ?? 0) <= 4).map((x) => x.range),
  );
  const want = Math.max(floorD, Math.min(style.spacing, reachMax * 0.9));
  if (dist > want + 0.9) {
    // Too far: close in; a gap-closer from range sometimes.
    const rush = ready('attack').find((s) => s.dash && dist <= s.range && dist > 3);
    if (rush && rnd() < 0.18 + aggr * 0.25) return { ...IDLE, skill: rush.slot };
    // A jump-in from 6-9 m (jump, air dash, dive): the sly most.
    if (dist >= 6 && dist <= 9 && v.canAirDash && mem.air === 0 && rnd() < style.jumpIn * (0.08 + 0.03 * lv)) {
      mem.air = 1;
      return { ...IDLE, jump: true, move: [ux, uy] };
    }
    // A pause in the approach, as people do.
    if (v.t < mem.waitUntil) return { ...IDLE, move: side() };
    if (rnd() < 0.03) mem.waitUntil = v.t + 0.3 + rnd() * 0.4;
    return { ...IDLE, move: [ux, uy] };
  }

  // In the fighting range.
  if (v.t >= mem.waitUntil) {
    // Maybe a feint now.
    if (rnd() < style.feint * 0.07) {
      mem.feint = rnd() < 0.5 ? 'in' : 'out';
      mem.feintUntil = v.t + 0.25 + rnd() * 0.3;
      return { ...IDLE, move: mem.feint === 'in' ? [ux, uy] : away() };
    }
    // Strike?
    if (rnd() < STRIKE.base + aggr * STRIKE.aggr) {
      const atk = ready('attack').filter((s) => inReach(s) && !s.dash);
      // A heavy move now and then, the basic cut otherwise: a person mixes.
      if (atk.length && rnd() < 0.55) {
        const pick = atk[Math.floor(rnd() * atk.length)]!;
        return { ...IDLE, skill: pick.slot };
      }
      // A break on an open foe is weak: only as a bluff (a tell that draws a reaction), seldom.
      const brk = ready('break').filter(inReach)[0];
      if (brk && rnd() < style.feint * 0.2) return { ...IDLE, skill: brk.slot };
      if (dist <= reachB + 0.3) {
        mem.waitUntil = v.t + STRIKE.pause * (0.25 + rnd() * 0.25);
        return { ...IDLE, engage: true };
      }
    }
    // Hold off for a beat after a strike.
    if (rnd() < STRIKE.hold) mem.waitUntil = v.t + 0.3 + rnd() * 0.5;
  }
  // Lull: keep the spacing and circle.
  if (dist < want - 0.5) return { ...IDLE, move: away(0.8) };
  if (dist > want + 0.4) return { ...IDLE, move: [ux, uy] };
  return { ...IDLE, move: side(), engage: false };
}
